<svelte:options runes={false} />

<script>
  import { minorToMoney } from '../../api/invoices';
  import { formatHours, withCurrency } from '../format.mjs';
  import StatRow from './StatRow.svelte';

  export let hourlyRate;
  export let flatAddition;
  export let flatAdditionReason;
  export let payerEmail;
  export let payerName;
  export let payerCompany;
  export let providerId;
  export let availableProviders;
  export let selectedProvider;
  export let quote;
  export let currency;
  export let selectedHours;
  export let payerFeeQuoteAvailable;
  export let minHourlyRateMinor;
  export let suggestedAtMinimumMinor;
  export let reportedHours;
  export let ledgerAggregate = null;
  export let blockers;
  export let onSubmit;

  // The submit button is never disabled. A disabled control gives no feedback on
  // touch and never says why, so pressing it reveals what is still missing.
  let showBlockers = false;

  $: askingRate = minorToMoney(minHourlyRateMinor || 100, currency);
  // The reference range gives a payer something besides the ask to anchor on.
  // Since 2026-08-09 the server publishes it from the first settled
  // contributor (the hide-until-a-second-payer thresholds were dropped after
  // the S02 read), so a null here means nobody has paid yet. At one distinct
  // rate a "range" of $30–$30 is nonsense, so it renders as a single figure.
  $: rateRange = ledgerAggregate?.paidRateLowMinor > 0 && ledgerAggregate?.paidRateHighMinor > 0
    ? {
        low: minorToMoney(ledgerAggregate.paidRateLowMinor, currency),
        high: minorToMoney(ledgerAggregate.paidRateHighMinor, currency),
        single: ledgerAggregate.paidRateLowMinor === ledgerAggregate.paidRateHighMinor,
      }
    : null;

  function handleSubmit() {
    if (blockers.length > 0) {
      showBlockers = true;
      return;
    }
    showBlockers = false;
    onSubmit();
  }
</script>

<section class="payer-step" aria-labelledby="payer-rate-heading">
  <p class="step-label">Step 2 · Your rate</p>
  <h3 id="payer-rate-heading">What was an hour of this worth to you?</h3>

  <div class="rate-field">
    <label>
      Your rate, per hour
      <!-- Pre-filled with the asking rate (decided 2026-08-08/09). The ask is
           a watermark, not a floor: the payer edits in either direction, and
           the sentence below says so. -->
      <input
        bind:value={hourlyRate}
        min="1"
        step="1"
        type="number"
        inputmode="decimal"
      />
    </label>
  </div>
  <p class="quiet rate-reference">
    Asking <span class="money">{askingRate}</span> an hour — pay more or less as you see fit.
    {#if suggestedAtMinimumMinor}
      At that rate, all {formatHours(reportedHours)} comes to
      <span class="money">{minorToMoney(suggestedAtMinimumMinor, currency)}</span>.
    {/if}
    {#if rateRange}
      {#if rateRange.single}
        Others have chosen <span class="money">{rateRange.low}</span> an hour.
      {:else}
        Others have chosen <span class="money">{rateRange.low}</span>–<span class="money">{rateRange.high}</span> an hour.
      {/if}
    {/if}
    There is no maximum.
  </p>

  <details class="payer-extra">
    <summary>Add a fixed amount on top (optional)</summary>
    <div class="payer-extra-body">
      <label>
        Amount to add
        <input bind:value={flatAddition} min="0" step="0.01" type="number" inputmode="decimal" />
      </label>
      <label>
        What's it for? (optional)
        <textarea bind:value={flatAdditionReason} rows="2"></textarea>
      </label>
    </div>
  </details>
</section>

<section class="payer-step" aria-labelledby="payer-receipt-heading">
  <p class="step-label">Step 3 · Where your receipt goes</p>
  <h3 id="payer-receipt-heading">Your details</h3>
  <div class="form-grid">
    <label>Email<input bind:value={payerEmail} type="email" placeholder="name@example.com" /></label>
    <label>Name (optional)<input bind:value={payerName} /></label>
    <label>Company (optional)<input bind:value={payerCompany} /></label>
    {#if availableProviders.length > 1}
      <label>
        How you'd like to pay
        <select bind:value={providerId}>
          {#each availableProviders as provider (provider.providerId)}
            <option value={provider.providerId}>{provider.label}</option>
          {/each}
        </select>
      </label>
    {/if}
  </div>
</section>

<section class="payer-total" aria-label="What you'll pay">
  <StatRow
    grand
    label="Total"
    value={withCurrency(minorToMoney(quote.chargeTotalMinor, currency), currency)}
  />

  <details class="payer-breakdown">
    <summary>Show breakdown</summary>
    <div class="totals">
      <StatRow label="Hours you're paying for" value={formatHours(selectedHours)} />
      <StatRow label="Work" value={minorToMoney(quote.baseMinor, currency)} />
      <StatRow label="Costs" value={minorToMoney(quote.fixedCostsMinor, currency)} />
      <StatRow label="Amount you added" value={minorToMoney(quote.flatAdditionMinor, currency)} />
      <StatRow label="Tax" value={minorToMoney(quote.taxMinor, currency)} />
      <StatRow
        label="Card processing (estimated)"
        value={payerFeeQuoteAvailable
          ? minorToMoney(quote.quotedPayerProcessorFeeMinor, currency)
          : 'not estimated'}
      />
    </div>
    {#if selectedProvider?.feeQuoteDisclaimer}
      <p class="quiet">{selectedProvider.feeQuoteDisclaimer}</p>
    {/if}
    {#if providerId === 'stripe' && !payerFeeQuoteAvailable}
      <p class="quiet">No compatible processor-fee estimate is configured, so no processor-fee contribution is added to this payment.</p>
    {/if}
  </details>

  {#if showBlockers && blockers.length > 0}
    <div class="notice blocker-list" role="alert">
      <strong>Before you can pay:</strong>
      <ul>
        {#each blockers as blocker}
          <li>{blocker}</li>
        {/each}
      </ul>
    </div>
  {/if}

  <button class="primary payer-submit" type="button" on:click={handleSubmit}>
    Pay {withCurrency(minorToMoney(quote.chargeTotalMinor, currency), currency)}
  </button>
  <p class="quiet">
    Your details are saved here first. You'll review them again on the secure
    payment page before anything is charged.
  </p>
</section>
